import React from 'react';
import { Button, Checkbox } from 'components/atoms/index';
import * as styles from './Table.css';
import { Icon } from 'icons';

interface WordData {
  id: string;
  word: string;
  meaning: string;
  ttsType: string;
  isPublic?: boolean;
  registrant?: string;
  createdAt?: string;
}

interface WordTableProps {
  data: WordData[];
  onDelete?: (id: string) => void;
  onEdit?: (id: string) => void;
  showActions?: boolean;
  showCheckbox?: boolean;
  showTtsType?: boolean;
  selectedIds?: string[];
  onSelectionChange?: (id: string, checked: boolean) => void;
}

const mapTtsToKorean = (tts: string) => {
  const parts = (tts || '').split('/').map(s => (s || '').trim());
  const [genderRaw = '', ageRaw = '', speedRaw = '', pitchRaw = ''] = parts;

  const toGender = (v: string) => (
    v === 'male' ? '남성' : v === 'female' ? '여성' : (v || '')
  );
  const toAge = (v: string) => (
    v === 'child' ? '아동' : v === 'young' ? '청소년' : v === 'adult' ? '성인' : v === 'elder' ? '노년' : (v || '')
  );
  const toSpeed = (v: string) => (
    v === 'slow' ? '느림' : v === 'normal' ? '보통' : v === 'fast' ? '빠름' : (v || '')
  );
  const toPitch = (v: string) => (
    v === 'low' ? '낮음' : v === 'medium' ? '보통' : v === 'high' ? '높음' : (v || '')
  );

  const genderText = toGender(genderRaw);
  const ageText = toAge(ageRaw);
  const speedText = toSpeed(speedRaw);
  const pitchText = toPitch(pitchRaw);

  return (
    <div className='text-[12px] leading-[1.4] text-gray-600'>
      <div>{genderText}{genderText && ageText ? ' / ' : ''}{ageText}</div>
      <div>{speedText}{speedText && pitchText ? ' / ' : ''}{pitchText}</div>
    </div>
  );
};

type CheckboxColumn = {
  key: 'checkbox';
  render: (
    row: WordData,
    index: number,
    showCheckbox: boolean,
    selectedIds: string[],
    onSelectionChange?: (id: string, checked: boolean) => void
  ) => React.ReactNode;
};

type ActionColumn = {
  key: 'actions';
  render: (
    row: WordData,
    index: number,
    showActions: boolean,
    onDelete?: (id: string) => void,
    onEdit?: (id: string) => void
  ) => React.ReactNode;
};

type NumberColumn = {
  key: 'no';
  render: (_row: WordData, index: number) => number;
};

type DefaultColumn = {
  key: 'word' | 'meaning' | 'ttsType';
  render: (row: WordData) => React.ReactNode;
};

type ColumnConfig = CheckboxColumn | ActionColumn | NumberColumn | DefaultColumn;

// 컬럼 렌더링용 메타 정보
const columns: ColumnConfig[] = [
  { 
    key: 'checkbox' as const, 
    render: (row: WordData, index: number, showCheckbox: boolean, selectedIds: string[], onSelectionChange?: (id: string, checked: boolean) => void) => {
      if (!showCheckbox) return null;
      return (
        <Checkbox
          checked={selectedIds?.includes(row.id) || false}
          onChange={(checked: boolean) => onSelectionChange?.(row.id, checked)}
        />
      );
    }
  },
  { 
    key: 'no' as const, 
    render: (_row: WordData, index: number) => index + 1 
  },
  { 
    key: 'word' as const, 
    render: (row: WordData) => (
      <div className=''>{row.word}</div>
    )
  },
  { 
    key: 'meaning' as const, 
    render: (row: WordData) => (
      <div className=''>{row.meaning}</div>
    )
  },
  { 
    key: 'ttsType' as const, 
    render: (row: WordData) => mapTtsToKorean(row.ttsType)
  },
  { 
    key: 'actions' as const, 
    render: (row: WordData, index: number, showActions: boolean, onDelete?: (id: string) => void, onEdit?: (id: string) => void) => {
      if (!showActions) return null;
      return (
        <div className={styles.actionButton}>
          {onEdit && (
            <Button
              text
              size="small"
              onClick={() => onEdit(row.id)}
              style={{ marginRight: '0.5rem' }}
            >
              수정
            </Button>
          )}
          {onDelete && (
            <Button
              text
              intent="danger"
              size="small"
              onClick={() => onDelete(row.id)}
            >
              삭제
            </Button>
          )}
        </div>
      );
    }
  },
];

export default function WordTable({ data, onDelete, onEdit, showActions = true, showCheckbox = false, showTtsType = true, selectedIds = [], onSelectionChange }: WordTableProps) {
  if (data.length === 0) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '3rem 1rem',
        color: '#666',
        fontSize: '1rem'
      }}>
        단어가 없습니다.
      </div>
    );
  }

  return (
    <div>
      {/* 테이블 헤더 (PC용) */}
      <ul className={styles.headerList}>
        {showCheckbox && (
          <li className={`${styles.headerItem} max-w-[80px] justify-center`}>선택</li>
        )}
        <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
        <li className={`${styles.headerItem}`}>단어</li>
        <li className={`${styles.headerItem}`}>해석</li>
        {showTtsType && (
        <li className={`${styles.headerItem} max-w-[200px]`}>TTS 타입</li>
        )}
        {showActions && (
          <li className={`${styles.headerItem} max-w-[120px] justify-center`}>작업</li>
        )}
      </ul>

      {/* 테이블 바디 */}
      {data.map((row, index) => {
        const isSelected = selectedIds?.includes(row.id) || false;
        
        const handleRowClick = () => {
          if (showCheckbox && onSelectionChange) {
            onSelectionChange(row.id, !isSelected);
          }
        };
        
        return (
          <div 
            key={row.id} 
            className={`${styles.mobileCard} ${showCheckbox ? `${styles.hoverRow} cursor-pointer` : ''} ${isSelected ? styles.selectedRow : ''}`}
            onClick={showCheckbox ? handleRowClick : undefined}
          >
            <ul className={styles.bodyList}>
      {columns.map(column => {
        const { key } = column;
                if (key === 'actions' && !showActions) return null;
                if (key === 'ttsType' && !showTtsType) return null;
                if (key === 'checkbox' && !showCheckbox) return null;
                
                return (
                  <li
                    key={key}
                    className={`${styles.bodyItem} ${
                      key === 'checkbox' ? 'max-w-[80px] hidden lg:flex justify-center' :
                      key === 'no' ? 'max-w-[80px] hidden lg:flex justify-center' :
                      key === 'word' ? 'lg:flex-1' :
                      key === 'meaning' ? 'text-sm text-gray-500 lg:flex-1' :
                      key === 'ttsType' ? 'text-sm text-gray-500 max-w-[200px] lg:static' :
                      key === 'actions' ? 'absolute top-5 right-5 lg:static max-w-[120px] lg:flex justify-center' :
                      ''
                    }`}
                  >
                    {key === 'checkbox'
                      ? column.render(row, index, showCheckbox, selectedIds, onSelectionChange)
                      : key === 'actions'
                        ? column.render(row, index, showActions, onDelete, onEdit)
                        : key === 'no'
                          ? (data.length > 0 ? data.length - index : 0)
                          : column.render(row)}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
} 